import { createTheme, ThemeOptions, styled } from '@mui/material/styles';
import { LinkProps } from '@mui/material/Link';
import Button, { ButtonProps } from "@mui/material/Button";
import Box from "@mui/material/Box";

// Define custom theme interface
declare module '@mui/material/styles' {
   interface Theme {
      dotaSpotto: {
         font: string;
         font2: string;
         primary: string;
      };
   }
   // Allow configuration using `createTheme`
   interface ThemeOptions {
      dotaSpotto?: {
         font?: string;
         font2?: string;
         primary?: string;
      };
   }

   // Extend Link variants
   interface LinkPropsVariantOverrides {
      button: true;
      gold: true;
   }
}

const chipHeight = 60;


export const dotaSpottoTheme = {
   font: `'Rokkitt', 'sans-serif'`,
   font2: `'Cardo', 'serif'`,
   primary: "#A72714"
};

const DotaSpottoFullTheme: ThemeOptions = {
   dotaSpotto: dotaSpottoTheme,
   components: {
      MuiButton: {
         styleOverrides: {
            root: {
               lineHeight: 1,
            },
            contained: {
               backgroundColor: dotaSpottoTheme.primary,
               color: "#FFF",
               fontFamily: dotaSpottoTheme.font,
               fontWeight: 700,
               height: 40,
               border: `1px solid ${dotaSpottoTheme.primary}`,
               lineHeight: 1,
               ":hover": {
                  backgroundColor: "#000",
                  border: "1px solid gold",
               },
            },
            text: {
               fontFamily: dotaSpottoTheme.font,
               ":hover": {
                  color: "white"
               }
            }
         },
      },
      MuiAutocomplete: {
         styleOverrides: {
            root: {
               color: "#FFF",
               backgroundColor: "transparent",
               fontFamily: dotaSpottoTheme.font2,
            },
            popper: {
               backgroundColor: "#FFF",
            },
            groupLabel: {
               fontWeight: 800,
               color: `white`,
               backgroundImage: `linear-gradient(69deg, ${dotaSpottoTheme.primary}, #8D3020,#AB2E17, gold)`,
            },
            groupUl: {
               fontFamily: dotaSpottoTheme.font2,
               backgroundColor: `#000`,
            },
            popupIndicator: {
               backgroundColor: "silver",
               ":hover": {
                  backgroundColor: "gold",
               },
            },
            paper: {
               backgroundColor: 'white'
            },
            option: {
               backgroundColor: dotaSpottoTheme.primary,
               ":hover": {
                  backgroundColor: dotaSpottoTheme.primary,
               },
            },
         },
      },
      MuiOutlinedInput: {
         styleOverrides: {
            notchedOutline: {
               borderColor: dotaSpottoTheme.primary,
            },
            root: {
               color: `white`,
               backgroundColor: `transparent`,
               fontFamily: dotaSpottoTheme.font,
               ":hover, &.Mui-focused": {
                  ".MuiOutlinedInput-notchedOutline": {
                     borderColor: "gold",
                  },
               },
            },
         },
      },
      MuiFormLabel: {
         styleOverrides: {
            root: {
               fontFamily: dotaSpottoTheme.font2,
               color: "#FFF",
               "&.Mui-focused": {
                  color: "gold",
                  borderColor: "gold",
               },
            },
         },
      },
      MuiChip: {
         styleOverrides: {
            root: {
               ":hover": {
                  backgroundColor: dotaSpottoTheme.primary,
               },
            },
            avatar: {
               height: chipHeight,
               width: chipHeight,
               marginLeft: 0,
            },
            label: {
               fontSize: "1.25em",
               marginLeft: 10,
               marginRight: 10,
               fontFamily: dotaSpottoTheme.font2,
            },
            clickableColorSecondary: {
               backgroundColor: "green",
            },
            filledPrimary: {
               backgroundColor: "#000",
               backgroundImage: "linear-gradient(75deg, ${dotaSpottoTheme.primary}, gold)",
               borderRadius: chipHeight,
               height: chipHeight,
            },
         },
      },
      MuiLink: {
         variants: [
            {
               props: { variant: 'button' } as LinkProps,
               style: {
                  backgroundColor: dotaSpottoTheme.primary,
                  color: "#FFF",
                  fontFamily: dotaSpottoTheme.font,
                  fontWeight: 700,
                  padding: '10px 15px',
                  border: `1px solid ${dotaSpottoTheme.primary}`,
                  borderRadius: 5,
                  textDecoration: 'none',
                  display: 'inline-block',
                  '&:hover': {
                     backgroundColor: "#000",
                     border: "1px solid gold",
                     textDecoration: 'none',
                  },
               },
            },
            //   {
            //     props: { variant: 'gold' } as LinkProps,
            //     style: {
            //       color: "gold",
            //       textDecoration: 'none',
            //       '&:hover': {
            //         color: dotaSpottoTheme.primary,
            //         textDecoration: 'none',
            //       },
            //     },
            //   },
         ],
         styleOverrides: {
            root: {
               textDecoration: 'none',
               '&:hover': {
                  textDecoration: 'none',
               },
            },
         },
      },
      MuiContainer: {
         styleOverrides: {
            root: {
               maxWidth: "1440px",
            },
         },
      },
   },
   palette: {
      mode: 'dark',
      background: {
         default: "#000"
      },
      primary: {
         main: dotaSpottoTheme.primary,
      },
      error: {
         main: "#ffb3ae",
         contrastText: "#1a0000",
      },
   },
   typography: {
      fontFamily: dotaSpottoTheme.font,
      body1: {
         color: 'white',
      },
      h1: {
         fontFamily: dotaSpottoTheme.font2,
         color: 'white',
      },
      h2: {
         fontFamily: dotaSpottoTheme.font2,
         color: 'white',
      },
      h3: {
         fontFamily: dotaSpottoTheme.font2,
         color: 'white',
      },
   },
}

const GoldButton = styled(Button)({
   backgroundColor: "gold",
   color: "#000",
   fontFamily: dotaSpottoTheme.font,
   fontWeight: 700,
   height: 40,
   lineHeight: 1.1,
   ":hover": {
      backgroundColor: "#000",
      color: "gold",
   },
});

const heroSelectorStyles = {
   display: "flex",
   flexDirection: "column",
   minHeight: "calc(100vh)",
   backgroundColor: "black",
   position: "relative",
   zIndex: 1,
}

const HeroVideoBackground = styled(Box)(({ theme }) => ({
   position: "fixed",
   zIndex: -1,
   width: "100vw",
   // height: "290vh",
   // top: "-30vh",
   [theme.breakpoints.up("xs")]: {
      height: "120vh",
      top: "-10vh",
   },
   [theme.breakpoints.up("md")]: {
      height: "220vh",
      top: "-20vh",
   },
   left: "0",
   display: "flex",
   justifyContent: "center",
   pointerEvents: "none",
   opacity: 0.35,
   filter: "saturate(11) blur(10px) contrast(1.2)",
   transition: "filter 1s ease, opacity 1s ease, height 0.5s ease, top 0.5s ease",
   overflow: "hidden", // make sure the pseudo stays contained
   "&[data-active]": {
      opacity: 0.5,
      filter: "saturate(1) blur(0) contrast(1)",
      height: '100vh',
      top: 0,
      animation: "shadowCosmic 8s ease-in-out infinite",
      "&::before": {
         content: '""',
         position: "absolute",
         width: "200%",
         height: "200%",
         top: "-50%",
         left: "-50%",
         background: "radial-gradient(circle at center, rgba(255, 255, 255, 0.3) 0%, transparent 60%)",
         animation: "pulseGlow 6s ease-in-out infinite",
         zIndex: -1, // behind everything else
         borderRadius: "50%",
         pointerEvents: "none",
      },
   },
   "@keyframes shadowCosmic": {
      "0%": {
         filter: "saturate(1) blur(0) contrast(1) drop-shadow(12px 12px 16px #A72714)",
      },
      "20%": {
         filter: "saturate(1) blur(0) contrast(1) drop-shadow(-20px 14px 28px #FFD700)",
      },
      "40%": {
         filter: "saturate(1) blur(0) contrast(1) drop-shadow(-18px -20px 10px #4FC3F7)",
      },
      "60%": {
         filter: "saturate(1) blur(0) contrast(1) drop-shadow(22px -18px 30px #9C27B0)",
      },
      "80%": {
         filter: "saturate(1) blur(0) contrast(1) drop-shadow(16px 22px 14px #00E676)",
      },
      "100%": {
         filter: "saturate(1) blur(0) contrast(1) drop-shadow(12px 12px 16px #A72714)",
      },
   },
   "@keyframes pulseGlow": {
      "0%": {
         transform: "scale(1)",
         opacity: 0.6,
      },
      "50%": {
         transform: "scale(1.2)",
         opacity: 0.2,
      },
      "100%": {
         transform: "scale(1)",
         opacity: 0.6,
      },
   },
}));


export const theme = createTheme({
   ...DotaSpottoFullTheme,
   dotaSpotto: dotaSpottoTheme
});

export { GoldButton, heroSelectorStyles, HeroVideoBackground };
